/**
 * Pulser Studio specific storage format, saves experiment data, including all state
 * that cannot be stored in pulser export format.
 */

import { Ok } from 'ts-results';
import { v4 as uuid } from 'uuid';

import * as deviceModel from '~/models/device';
import { assert, assertNever } from '~/util';
import { Basis, getChannelKind } from './channel';
import type * as schema from '../pulser-schema';

import type { Channel } from './channel';
import type { Device } from './device';
import type { Pulse } from './pulse';
import type { Atom } from './register';
import type { Result } from 'ts-results';
import type { ValidationError } from '~/util/validation';

export * from './waveform';
export * from './register';
export * from './channel';
export * from './pulse';
export * from './device';
export * from './commands';

/**
 * Import basis from Pulser textual format into the internal Basis type.
 */
export function importBasis(basis: schema.Basis): Basis {
  switch (basis) {
    case 'digital':
      return Basis.Digital;
    case 'ground-rydberg':
      return Basis.GroundRydberg;
    case 'XY':
      return Basis.XY;
    default:
      assertNever(
        basis,
        `Channel basis ${basis} is not yet supported. Currently only 'digital', 'ground-rydberg', and 'xy' channels are supported.`
      );
  }
}

export type StudioExperiment = Readonly<{
  name: string;
  device: Device;
  register: Atom[];
  channels: Channel[];
  pulses: Pulse[];
  basis: Basis | null;
  metadata?: schema.PulserSequence['metadata'];
}>;

/**
 * Returns the hardware channel identifier, based on kind and addressing (ex: rydberg_local)
 */
export function getHardwareChannelId(
  ch: Omit<Channel, 'label' | 'uuid'>
): schema.HardwareChannel {
  const kind = getChannelKind(ch);
  if (kind === 'microwave') {
    return 'mw_global';
  }
  return `${kind}_${ch.addressing}`;
}

/**
 * Get initial experiment data to load when opening the application for the first time
 * or when the user wants to create a new experiment.
 */
export function defaultData(): StudioExperiment {
  const device = { ...deviceModel.devices.MockDevice, name: 'MockDevice' };
  assert(device !== undefined);
  const deviceChannels = deviceModel.deviceChannels.MockDevice;
  assert(deviceChannels !== undefined);
  return {
    name: 'Unnamed experiment',
    device: device,
    register: [],
    channels: Object.values(deviceChannels).map((channel, idx) => ({
      label: `ch${idx}`,
      uuid: uuid(),
      ...channel
    })),
    pulses: [],
    basis: null
  };
}

/**
 * Import and validate a Pulser Studio experiment from unknown JS object.
 *
 * Safe to use on unknown object straight after JSON parsing.
 */
export function parseStudioExperiment(
  jsValue: unknown
): Result<StudioExperiment, ValidationError> {
  return Ok(jsValue as StudioExperiment);
  // BB: removed some Zod-based format validation here.
}
